import { memo } from 'react'

import { cn } from '@/utils'
import { useAtomValue } from 'jotai'

import { RundownPermission } from '~shared/rundown/permission'
import { RunnerState } from '~shared/types'

import { runnerStateAtom } from '@/store/runner.store'

import { useRundownPermission } from '@/features/permissions/lib/use-rundown-permission'
import { useRundown } from '@/features/rundown/rundown.derived'

import { useIsActiveCue, useIsRunnerNextCue } from './cue.hooks'

type CueIndexProps = {
  cueId: string
  className?: string
  index: string | number
  readonly?: boolean
  loading?: boolean
  onSetNext?: () => void
  onJumpToCue?: () => void
  skipDuringShow?: boolean
}

export const CueIndex = memo(function CueIndex({
  cueId,
  className,
  index,
  readonly,
  loading,
  onSetNext,
  onJumpToCue,
  skipDuringShow,
}: CueIndexProps) {
  // Note: We avoid using the timestamp to avoid frequent re-renders
  const isActive = useIsActiveCue(cueId)
  const isRunnerNext = useIsRunnerNextCue(cueId)
  const isOnAir = useAtomValue(runnerStateAtom) === RunnerState.ONAIR
  const rundown = useRundown()

  // On air the runner queues the next cue. Before the show starts (and after it
  // ends) "Cue up next" writes the rundown's startCueId.
  const isNext = isOnAir ? isRunnerNext : rundown.startCueId === cueId

  const isShowCaller = useRundownPermission({
    permission: RundownPermission.SHOWCALL,
  })

  return (
    <button
      title="Cue up next"
      className={[
        'relative h-full w-full flex justify-center font-mono text-sm border-2 border-transparent rounded-sm transition-color focus:outline-hidden focus:ring-3',
        isNext && 'border-gray-400!',
        !(readonly || isNext || isActive) && 'hover:border-white/50',
        'disabled:pointer-events-none',
        skipDuringShow && 'border-gray-800! border-dashed',
        className,
      ].join(' ')}
      onClick={onSetNext}
      onDoubleClick={onJumpToCue}
      disabled={readonly || isActive || !isShowCaller || skipDuringShow}
    >
      {/* This DIV allows us to use "bg-animated" without the sticky span jumping around */}
      <div
        className={cn([
          'absolute h-full w-full rounded-sm',
          loading && 'bg-animated absolute! from-transparent to-white/20', // "absolute!" important is here as our bg-animated forces all children to be relative
        ])}
      />
      <span
        className={cn(
          'sticky top-27 h-min pt-8',
          String(index).length > 5 && 'text-xs',
        )}
      >
        {index}
      </span>
    </button>
  )
})
